import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MultiHookModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPlan?: string;
  currentPlanLabel?: string;
  currentPlanPrice?: string;
  isBooking: boolean;
  bookingWithMultiHook: boolean | null;
  onFinalBooking: (withMultiHook: boolean) => void;
}

const getMultiHookPricing = (planSlug?: string, currentPlanPrice?: string) => {
  const basePrice = currentPlanPrice || '0€';
  
  switch (planSlug) {
    case 'presence':
      return {
        multiHookPrice: '+200€ HT/mois',
        multiHookMonthly: 200,
        duration: 6,
        total: '1 200€ HT/mois',
        subTotal: '7 200€ HT pour 6 mois'
      };
    case 'emergence':
      return {
        multiHookPrice: '+1 000€ HT/mois',
        multiHookMonthly: 1000,
        duration: 2,
        total: '3 000€ HT/mois',
        subTotal: '6 000€ HT pour 2 mois'
      };
    case 'acceleration':
      return {
        multiHookPrice: '+1 000€ HT',
        multiHookMonthly: 1000,
        duration: 1,
        total: '6 000€ HT',
        subTotal: 'Paiement unique'
      };
    default:
      return {
        multiHookPrice: '+1 000€ HT',
        multiHookMonthly: 1000,
        duration: 1,
        total: '1 000€ HT',
        subTotal: 'Paiement unique'
      };
  }
};

export function MultiHookModal({
  open,
  onOpenChange,
  selectedPlan,
  currentPlanLabel,
  currentPlanPrice,
  isBooking,
  bookingWithMultiHook,
  onFinalBooking,
}: MultiHookModalProps) {
  const pricing = getMultiHookPricing(selectedPlan, currentPlanPrice);

  const handleFinalBooking = (withMultiHook: boolean) => {
    if (!isBooking) {
      onFinalBooking(withMultiHook);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn(
          "glass border-white/10 bg-black/95 backdrop-blur-xl text-white",
          "max-w-full w-full h-full pl-0 pr-0 pb-0 pt-10 rounded-none flex flex-col",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        )}
      >
        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-2xl font-semibold text-white">
              Augmentez votre portée de 40%
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-w-md mx-auto">
            {/* Première carte : Multi Hook description */}
            <div className="glass-strong border border-white/10 rounded-3xl p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Multi Hook
                  </h3>
                  <p className="text-2xl font-bold text-white">
                    50€ HT par vidéo
                  </p>
                </div>
                <Sparkles className="h-6 w-6 text-warning shrink-0" />
              </div>

              <p className="text-sm text-white/70 leading-relaxed">
                Démultipliez vos accroches, augmentez vos résultats.
              </p>

              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <span>3x plus de contenu à partir du même tournage</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <span>A/B testing de vos accroches pour optimiser la performance</span>
                </li>
                <li className="flex items-start gap-2 text-white/90">
                  <Check className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <span>Poussez plusieurs versions quand elles performent</span>
                </li>
              </ul>
            </div>

            {/* Deuxième carte : Pricing breakdown */}
            <div className="glass rounded-3xl p-6 space-y-4">
              <h3 className="text-base font-semibold text-white mb-3">
                Nouveau tarif avec Multi Hook
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Plan {currentPlanLabel}</span>
                  <span className="text-white font-medium">{currentPlanPrice}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Option Multi Hook</span>
                  <span className="text-warning font-medium">{pricing.multiHookPrice}</span>
                </div>

                <Separator className="bg-white/10 my-3" />

                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-white text-lg font-bold">{pricing.total}</span>
                </div>

                <p className="text-white/40 text-xs text-center pt-1">
                  {pricing.subTotal}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer fixe avec boutons */}
        <div className="border-t border-white/10 bg-black/95 backdrop-blur-xl p-6">
          <div className="space-y-3 max-w-md mx-auto">
            {/* Passer en premier */}
            <button
              onClick={() => handleFinalBooking(false)}
              disabled={isBooking}
              className={cn(
                "w-full rounded-2xl px-8 py-4 text-white/60 font-medium transition-all",
                "bg-white/5 hover:bg-white/10",
                "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              )}
            >
              {isBooking && bookingWithMultiHook === false ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Réservation...</span>
                </>
              ) : (
                'Passer'
              )}
            </button>

            {/* Réserver avec option ensuite */}
            <button
              onClick={() => handleFinalBooking(true)}
              disabled={isBooking}
              className={cn(
                "w-full rounded-2xl px-8 py-4 text-black font-medium transition-all",
                "bg-white hover:bg-white/90 active:scale-95",
                "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              )}
            >
              {isBooking && bookingWithMultiHook === true ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Réservation...</span>
                </>
              ) : (
                'Réserver avec option'
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
